import { useEffect, useMemo } from "react";
import { useUploadStore } from "@components/FileUpload/uploadStore";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import MarpolGridWasteDisposal from "@features/vessel-call/vessel-call-status/PortServices/form/waste-disposal/MarpolGridWasteDisposal";
import type { PortServicesFormData } from "@features/vessel-call/vessel-call-status/PortServices/schema/portServicesFormSchema";
import { usePortServicesFormStore } from "@features/vessel-call/vessel-call-status/PortServices/store/usePortServicesForm.store";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { useOffshoreServiceCompanies } from "@hooks/useOffshoreServiceCompanies";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import { FormProvider, type UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { useBerthAnchorageAreaOptions } from "../../hooks/useBerthAnchorageAreaOptions";
import { useFromToLocationOptions } from "../../hooks/useFromToLocationOptions";

type SlopsFieldsOutlet = {
  formData: UseFormReturn<PortServicesFormData>;
  updateFormData: (key: string, value: unknown) => void;
  selectedPortCode?: string;
};

const SLOPS_DOCUMENT_FIELDS: Array<keyof PortServicesFormData> = [
  "attachmentDocuments",
  "slopsDocument",
];

type CompanyType = {
  companyNameEnglish?: string;
  companyCode?: string;
};

const SlopsFields = ({ serviceType, langBase }: { serviceType: string; langBase: string }) => {
  const { formData, updateFormData, selectedPortCode } = useOutletContext<SlopsFieldsOutlet>();
  const { user } = useAuthStore();
  const { serviceCategoryCode, serviceTypeCode, serviceSubTypeCode } = usePortServicesFormStore();
  const { t } = useTranslation();
  const clearFilesForKey = useUploadStore((s) => s.clearFilesForKey);

  const { options: companies } = useFormattedReference<CompanyType>(
    `${REFERENCE_DATA_TYPES.COMPANIES}?isShippingAgency=true`,
    "bff-vessel-call",
  );

  const { options: offshoreCompanies = [] } = useOffshoreServiceCompanies(
    {
      portServiceCategoryCode: serviceCategoryCode,
      portServiceTypeCode: serviceTypeCode,
      portServiceSubtypeCode: serviceSubTypeCode,
    },
    "bff-vessel",
  );

  const serviceProviderOptions = useMemo(
    () =>
      offshoreCompanies.map(({ companyNameEnglish, companyCode }) => ({
        value: companyNameEnglish ?? companyCode ?? "",
        label: companyNameEnglish ?? companyCode ?? "",
      })),
    [offshoreCompanies],
  );

  const name = useMemo(
    () => companies.find((c) => c.companyCode === user?.companyId)?.companyNameEnglish ?? "",
    [companies, user?.companyId],
  );

  useEffect(() => {
    formData.setValue("registeredAgentName", "");
    formData.setValue("registeredAgentContactPerson", "");
    formData.setValue("serviceProviderName", "");
    formData.setValue("rts", "");
    formData.setValue("operationLocation", "");
    formData.setValue("berthOrAnchorageCode", "");
    formData.setValue("intendedOperationDetails", "");
    formData.setValue("wasteResiduesForDischargeFacility", []);
    SLOPS_DOCUMENT_FIELDS.forEach((fieldName) => {
      updateFormData(fieldName, []);
      clearFilesForKey(fieldName);
    });
  }, [serviceType]);

  useEffect(() => {
    formData.setValue("registeredAgentName", name);
    formData.setValue("registeredAgentContactPerson", user?.name ?? "");
  }, [name, user?.name]);

  const selectedLocation = formData.getValues("operationLocation");
  const { locations, fromToLocationOptions } = useFromToLocationOptions(
    ({ location, portCode }) => portCode === selectedPortCode && location === "ANCH",
  );
  const currentLocation = locations.find(
    (location) => location.fromToLocationCode === selectedLocation,
  );
  const {
    options: berthAnchorageAreaOptions,
    isLoading: isBerthAnchorageLoading,
    startLocationNavigation,
  } = useBerthAnchorageAreaOptions(
    currentLocation?.fromToLocationCode,
    currentLocation?.location,
    "operationLocation",
  );

  return (
    <FormProvider {...formData}>
      <FormSection title={t(`${langBase}.fields.title-registered-agent`)}>
        <FormItem<PortServicesFormData, "registeredAgentName">
          name="registeredAgentName"
          type="text"
          label={t(`${langBase}.fields.name`)}
          testId="portServices-registeredAgentName"
          onFieldChange={updateFormData}
        />
        <FormItem<PortServicesFormData, "registeredAgentContactPerson">
          name="registeredAgentContactPerson"
          type="text"
          label={t(`${langBase}.fields.contact-person`)}
          testId="portServices-registeredAgentContactPerson"
          onFieldChange={updateFormData}
        />
      </FormSection>
      <FormSection title={t(`${langBase}.fields.title-intended-operation`)}>
        <div className="grid grid-cols-1 gap-4 md:col-span-2 md:grid-cols-2">
          <FormItem<PortServicesFormData, "serviceProviderName">
            name="serviceProviderName"
            type="autocomplete"
            options={serviceProviderOptions}
            label={t(`${langBase}.fields.service-provider-name`)}
            testId="portServices-serviceProviderName"
            onFieldChange={updateFormData}
          />
          <FormItem<PortServicesFormData, "rts">
            name="rts"
            type="date-picker"
            label={t(`${langBase}.fields.rts`)}
            testId="portServices-rts"
            onFieldChange={updateFormData}
            timePicker
            combinedInput
            dateConstraint="not-past"
          />
        </div>
        <div className="grid grid-cols-1 gap-4 md:col-span-2 md:grid-cols-2">
          <FormItem<PortServicesFormData, "operationLocation">
            name="operationLocation"
            type="autocomplete"
            options={fromToLocationOptions}
            label={t(`${langBase}.fields.operation-location`)}
            testId="portServices-operationLocation"
            onFieldChange={(key, value) => {
              const nextLocation = locations.find(
                (location) => location.fromToLocationCode === value,
              );
              startLocationNavigation(nextLocation?.fromToLocationCode, nextLocation?.location);
              updateFormData(key, value);
              formData.setValue("berthOrAnchorageCode", "", { shouldDirty: true });
              updateFormData("berthOrAnchorageCode", "");
            }}
          />
          <FormItem<PortServicesFormData, "berthOrAnchorageCode">
            name="berthOrAnchorageCode"
            type="autocomplete"
            options={berthAnchorageAreaOptions}
            label={t(`${langBase}.fields.berth-anchorage-area-location`)}
            testId="berth-or-anchorage-code"
            onFieldChange={updateFormData}
            disabled={isBerthAnchorageLoading || !selectedLocation}
          />
        </div>
        <div className="md:col-span-2">
          <FormItem<PortServicesFormData, "intendedOperationDetails">
            name="intendedOperationDetails"
            type="textarea"
            maxChars={2500}
            minRows={3}
            label={t(`${langBase}.fields.details-of-intended-operation`)}
            testId="portServices-intendedOperationDetails"
            onFieldChange={updateFormData}
            fullWidth
            showCharCounter
          />
        </div>
        <div className="col-span-full">
          <MarpolGridWasteDisposal />
        </div>
      </FormSection>
    </FormProvider>
  );
};

export default SlopsFields;
